import React, { useState } from 'react';
import { Table, InputNumber, Popconfirm, message, Select, Space, Tooltip, Modal, Form } from 'antd';
import { DeleteOutlined, SaveOutlined, EditOutlined, CloseOutlined } from '@ant-design/icons';
import { useMutation } from 'react-apollo';
import CREATE_SLA_CONFIG from '../../../../../core/GraphQl/Mutations/CREATE_SLA_CONFIG';
import UPDATE_SLA_CONFIG from '../../../../../core/GraphQl/Mutations/UPDATE_SLA_CONFIG';
import DELETE_SLA_CONFIG from '../../../../../core/GraphQl/Mutations/DELETE_SLA_CONFIG';
import GET_SLA_CONFIGS from '../../../../../core/GraphQl/Queries/GET_SLA_CONFIGS';
import { taskTypes } from '../../configs/task.config';
import { space } from '../../configs/designTokens';

const { Option } = Select;

const SLAConfigTable = ({ slaConfigs = [], loading, addingNew = false, setAddingNew }) => {
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState(null);
  const [createForm] = Form.useForm();

  const refetchQueries = [{ query: GET_SLA_CONFIGS }];

  const [createSlaConfig, { loading: creating }] = useMutation(CREATE_SLA_CONFIG, {
    refetchQueries,
    onCompleted: () => {
      message.success('SLA configuration created');
      setAddingNew(false);
      createForm.resetFields();
    },
    onError: (err) => message.error(`Failed to create: ${err.message}`),
  });

  const [updateSlaConfig, { loading: updating }] = useMutation(UPDATE_SLA_CONFIG, {
    refetchQueries,
    onCompleted: () => {
      message.success('SLA configuration updated');
      setEditingId(null);
      setEditValue(null);
    },
    onError: (err) => message.error(`Failed to update: ${err.message}`),
  });

  const [deleteSlaConfig, { loading: deleting }] = useMutation(DELETE_SLA_CONFIG, {
    refetchQueries,
    onCompleted: () => message.success('SLA configuration deleted'),
    onError: (err) => message.error(`Failed to delete: ${err.message}`),
  });

  const availableTypes = taskTypes.filter(
    (type) => !slaConfigs.some((c) => c.requestType === type)
  );

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      await createSlaConfig({
        variables: {
          data: {
            requestType: values.requestType,
            recommendedDays: values.recommendedDays,
          },
        },
      });
    } catch (error) {
      if (error.errorFields) return;
      message.error('Failed to create: ' + error.message);
    }
  };

  const handleUpdate = (id) => {
    if (editValue === null || editValue === undefined) return;
    updateSlaConfig({
      variables: {
        where: { id },
        data: { recommendedDays: editValue },
      },
    });
  };

  const handleDelete = (id) => {
    deleteSlaConfig({
      variables: { where: { id } },
    });
  };

  const startEdit = (record) => {
    setEditingId(record.id);
    setEditValue(record.recommendedDays);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue(null);
  };

  const columns = [
    {
      title: 'Request Type',
      dataIndex: 'requestType',
      key: 'requestType',
      sorter: (a, b) => a.requestType.localeCompare(b.requestType),
      render: (val) => (
        <span className="task-table__name-text">{val}</span>
      ),
    },
    {
      title: 'Days',
      dataIndex: 'recommendedDays',
      key: 'recommendedDays',
      width: 120,
      sorter: (a, b) => a.recommendedDays - b.recommendedDays,
      render: (val, record) => {
        if (editingId === record.id) {
          return (
            <InputNumber
              min={1}
              max={365}
              value={editValue}
              onChange={setEditValue}
              size="small"
              autoFocus
            />
          );
        }
        return <strong>{val}</strong>;
      },
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      align: 'right',
      render: (_, record) => {
        if (editingId === record.id) {
          return (
            <Space size={4}>
              <Tooltip title="Save">
                <button className="task-table__action-btn" onClick={() => handleUpdate(record.id)} disabled={updating}>
                  <SaveOutlined />
                </button>
              </Tooltip>
              <Tooltip title="Cancel">
                <button className="task-table__action-btn" onClick={cancelEdit}>
                  <CloseOutlined />
                </button>
              </Tooltip>
            </Space>
          );
        }
        return (
          <Space size={4}>
            <Tooltip title="Edit">
              <button className="task-table__action-btn" onClick={() => startEdit(record)}>
                <EditOutlined />
              </button>
            </Tooltip>
            <Popconfirm
              title="Delete this SLA config?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete">
                <button className="task-table__action-btn" disabled={deleting}>
                  <DeleteOutlined />
                </button>
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="task-board-table-modern">
      <Table
        className="task-table"
        dataSource={slaConfigs}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="middle"
        showHeader={slaConfigs.length > 0}
        locale={{ emptyText: 'No SLA configurations yet. Click "Add SLA" to create one.' }}
      />

      <Modal
        className="task-create-modal"
        title="Add SLA Configuration"
        visible={addingNew}
        onOk={handleCreate}
        onCancel={() => {
          setAddingNew(false);
          createForm.resetFields();
        }}
        afterClose={() => createForm.resetFields()}
        confirmLoading={creating}
        width={400}
      >
        <Form form={createForm} layout="vertical" initialValues={{ recommendedDays: 3 }}>
          <Form.Item
            name="requestType"
            label="Request Type"
            rules={[{ required: true, message: 'Please select a request type' }]}
          >
            <Select placeholder="Select Request Type">
              {availableTypes.map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="recommendedDays"
            label="Recommended Days"
            rules={[{ required: true, message: 'Please enter the number of days' }]}
          >
            <InputNumber min={1} max={365} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SLAConfigTable;
